import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { LogOut, Menu, X } from "lucide-react";
import { openContactForm } from "@/lib/contactForm";
import { useAuth } from "@/lib/auth";
import { publicNavTo, visibleSiteNav } from "@/lib/publicNav";
import "./HeaderMobileMenu.css";

const MOBILE_NAV_ITEMS = visibleSiteNav();

function scrollToSection(sectionId) {
  document.getElementById(sectionId)?.scrollIntoView({ behavior: "smooth" });
}

export default function HeaderMobileMenu() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, isAdmin, isCustomer, logout, loading } = useAuth();

  const close = useCallback(() => setOpen(false), []);

  const authItems = useMemo(() => {
    if (loading || !isAuthenticated) return [];
    if (isAdmin) {
      return [
        { type: "link", to: "/admin", en: "Admin", ko: "관리자 페이지", testId: "header-mobile-admin-link" },
        { type: "logout", en: "Logout", ko: "로그아웃", testId: "header-mobile-logout-button" },
      ];
    }
    if (isCustomer) {
      return [
        { type: "link", to: "/mypage", en: "My Page", ko: "마이페이지", testId: "header-mobile-mypage-link" },
        { type: "logout", en: "Logout", ko: "로그아웃", testId: "header-mobile-logout-button" },
      ];
    }
    return [];
  }, [isAuthenticated, isAdmin, isCustomer, loading]);

  useEffect(() => {
    document.body.classList.toggle("ds-nav-open", open);
    return () => document.body.classList.remove("ds-nav-open");
  }, [open]);

  useEffect(() => {
    close();
  }, [location.pathname, location.hash, close]);

  const handleItem = (item) => {
    close();

    if (item.action === "contact") {
      openContactForm();
      return;
    }

    const page = item.page || "/";
    const { sectionId } = item;

    if (!sectionId) {
      if (location.pathname === page) {
        window.scrollTo({ top: 0, behavior: "smooth" });
        window.history.replaceState(null, "", page);
        return;
      }
      navigate(page);
      return;
    }

    if (location.pathname === page) {
      scrollToSection(sectionId);
      window.history.replaceState(null, "", `${page}#${sectionId}`);
      return;
    }

    navigate(`${page}#${sectionId}`);
  };

  const handleLogout = () => {
    close();
    logout();
  };

  return (
    <div className="ds-header-mobile-menu" data-testid="header-mobile-menu">
      <button
        type="button"
        className="ds-header-mobile-menu__toggle"
        data-testid="header-mobile-menu-toggle"
        aria-expanded={open}
        aria-controls="header-mobile-menu-panel"
        aria-label={open ? "메뉴 닫기" : "메뉴 열기"}
        onClick={() => setOpen((prev) => !prev)}
      >
        {open ? <X size={22} aria-hidden="true" /> : <Menu size={22} aria-hidden="true" />}
      </button>

      <div
        id="header-mobile-menu-panel"
        className={`ds-header-mobile-menu__overlay${open ? " is-open" : ""}`}
        aria-hidden={!open}
        onClick={close}
      >
        <div
          className="ds-header-mobile-menu__panel"
          role="dialog"
          aria-modal="true"
          aria-label="주요 메뉴"
          onClick={(e) => e.stopPropagation()}
        >
          <p className="ds-header-mobile-menu__eyebrow">Navigation</p>
          <ul className="ds-header-mobile-menu__list">
            {MOBILE_NAV_ITEMS.map((item) => (
              <li key={item.en}>
                {item.action === "contact" ? (
                  <button
                    type="button"
                    className="ds-header-mobile-menu__link"
                    data-testid="header-mobile-contact"
                    onClick={() => handleItem(item)}
                  >
                    <span className="ds-header-mobile-menu__link-en">{item.en}</span>
                    <span className="ds-header-mobile-menu__link-ko">{item.ko}</span>
                  </button>
                ) : (
                  <Link
                    to={publicNavTo(item)}
                    className="ds-header-mobile-menu__link"
                    onClick={() => handleItem(item)}
                  >
                    <span className="ds-header-mobile-menu__link-en">{item.en}</span>
                    <span className="ds-header-mobile-menu__link-ko">{item.ko}</span>
                  </Link>
                )}
              </li>
            ))}
          </ul>

          {authItems.length > 0 ? (
            <>
              <p className="ds-header-mobile-menu__eyebrow ds-header-mobile-menu__eyebrow--account">Account</p>
              <ul className="ds-header-mobile-menu__list">
                {authItems.map((item) => (
                  <li key={item.testId}>
                    {item.type === "link" ? (
                      <Link
                        to={item.to}
                        className="ds-header-mobile-menu__link"
                        data-testid={item.testId}
                        onClick={close}
                      >
                        <span className="ds-header-mobile-menu__link-en">{item.en}</span>
                        <span className="ds-header-mobile-menu__link-ko">{item.ko}</span>
                      </Link>
                    ) : (
                      <button
                        type="button"
                        className="ds-header-mobile-menu__link ds-header-mobile-menu__link--logout"
                        data-testid={item.testId}
                        onClick={handleLogout}
                      >
                        <LogOut size={14} aria-hidden="true" className="ds-header-mobile-menu__logout-icon" />
                        <span className="ds-header-mobile-menu__link-en">{item.en}</span>
                        <span className="ds-header-mobile-menu__link-ko">{item.ko}</span>
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}
